import { Injectable } from '@angular/core';
import { DataPersistenceService } from '../data-persistence/data-persistence.service';

@Injectable({
  providedIn: 'root'
})
export class GtmService {

  private scrollTimeout: any;
  private isTrackingEnabled = true;
  private currentDomain: string = 'proveedor';

  constructor(private dataPersistenceService: DataPersistenceService) { 
    this.initializeDataLayer();
  }

  /**
   * Inicia el tracking de scroll
   * @param domain - Dominio para el tracking
   */
  startScrollTracking(domain: string): void {
    this.currentDomain = domain;
    this.isTrackingEnabled = true;
    this.initializeEventListeners();
  }

  /**
   * Detiene el tracking de scroll
   */
  stopScrollTracking(): void {
    this.isTrackingEnabled = false;
    if (typeof window !== 'undefined') {
      window.removeEventListener('scroll', () => this.handleScroll());
    }
  }

  /**
   * Inicializa el datalayer si no existe
   */
  private initializeDataLayer(): void {
    if (typeof window !== 'undefined' && !window.dataLayer) {
      window.dataLayer = window.dataLayer || [];
    }
  }

  /**
   * Inicializa los listeners de eventos para scroll
   */
  private initializeEventListeners(): void {
    if (typeof window !== 'undefined') {
      // ? Listener para scroll con debounce
      window.addEventListener('scroll', () => {
        this.handleScroll();
      }, { passive: true });
    }
  }

  /**
   * Maneja eventos de scroll con debounce para evitar demasiados eventos
   */
  private handleScroll(): void {
    if (!this.isTrackingEnabled) return;

    // ? Debounce para evitar spam de eventos de scroll
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;
      const scrollPercentage = Math.round((scrollTop / (scrollHeight - clientHeight)) * 100);

      // ! Evento GTM
      this.push({
        dominio: this.currentDomain,
        accion: 'scroll',
        interaccion: 'scroll',
        componente_ui: 'scroll',
        valor: scrollPercentage,
        scroll_percentage: scrollPercentage,
        scroll_position: scrollTop,
        page_url: window.location.href,
        page_title: document.title,
        resultado_accion: 'exisoso',
      });
    }, 500);
  }

  /**
   * Envía datos al datalayer
   * @param data - Datos a enviar al datalayer
   */
  async push(data: any): Promise<void> {
    if (typeof window !== 'undefined' && window.dataLayer) {
      const id_token = this.dataPersistenceService.getIdTokenData();
      const gentro_gestion = this.dataPersistenceService.getCentroGestionCookie();
      const usuario_id = this.dataPersistenceService.getUsername();
      const empresa_id = this.dataPersistenceService.getCompanyIdCookie();
      const empresa_rut = this.dataPersistenceService.getCompanyIdentificationNumberCookie();
      const modulo = "registro";
      const session_id = id_token?.sub || this.dataPersistenceService.getSessionCookie() || '';
      const session_start = id_token?.iat || null;
      const currentTimeInSeconds = Math.floor(Date.now() / 1000);
      const session_duration = session_start ? (currentTimeInSeconds - session_start) : 0;
      const first_seen = localStorage.getItem('first_seen');
      const last_seen = new Date().toISOString();
      const correo = id_token?.email || '';
      const plan = null;

      const body = {
        ...data,
        event: `${modulo}_${data.dominio}_${data.accion}`,
        usuario_id: usuario_id,
        empresa_id: empresa_id,
        empresa_rut: empresa_rut,
        modulo: modulo,
        centro_gestion: gentro_gestion,
        session_id: session_id,
        session_start: session_start,
        session_duration: session_duration,
        first_seen: first_seen,
        last_seen: last_seen,
        correo: correo,
        plan: plan,
      }

      try {
        window.dataLayer.push(body);
      } catch {
        // swallow push errors to avoid breaking app flow
      }
    }
  }
}

// Extensión de la interfaz Window para incluir dataLayer
declare global {
  interface Window {
    dataLayer: any[];
  }
}
